<script lang="ts">
import type { RouteRecordNormalized } from 'vue-router'
import { getRsaSign } from '@/api/common'
import { MENU_KEY } from '@/enums/cache'
import { useUserStore } from '@/store/modules/user'
import { openWindow } from '@/utils'
import { getAuthCache } from '@/utils/auth'
import useMenu from '@/hooks/useMenu'
import dayjs from 'dayjs'
import { matchMenu } from '@/router/menuHelper'
import type { MenuRoute } from '@/router/menuHelper/type'
import { PAY_CENTER_PATH } from '@/utils/micro-app'

export default {
  name: 'LayoutSider'
}
</script>
<script lang="ts" setup>
const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false
  },
  hidden: {
    type: Boolean
  }
})
const router = useRouter()
const route = useRoute()
const openKeys = ref<string[]>([])
const selectedKeys = computed<string[]>(() => {
  const {
    hash,
    matched,
    meta: { parentMenu }
  } = route
  if (parentMenu) {
    return [parentMenu as string]
  }
  const path = matched[matched.length - 1].path
  if (path === PAY_CENTER_PATH && hash) {
    // 结算系统内嵌页
    const hashWithoutQuery = hash.split('?')[0]
    return [`${path}${hashWithoutQuery}`]
  }
  return [path]
})

const userStore = useUserStore()
const menuRoutes = matchMenu(getAuthCache(MENU_KEY))
openKeys.value = menuRoutes.map((item) => item.route.path)
const menuStore = useMenu()
menuStore.init(menuRoutes)

watch(
  () => route.matched,
  () => {
    const { matched } = route
    const list = matched.map((i) => i.path)
    list.length = matched.length - 1
    openKeys.value = [...new Set([...openKeys.value, ...list])] // 去重
  },
  { immediate: true }
)

const to = (path: string): void => {
  router.push(path)
}

async function getRsaSignOfAccount(account: string) {
  const expireTimestamp = Math.round(dayjs().add(10, 'minute').valueOf() / 1000)
  const data = await getRsaSign({
    account,
    expireTimestamp
  })
  return data
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleSelectMenu(item: RouteRecordNormalized) {
  const isOpenNewWindow = item.meta?.openInNewWindow
  const path = item.path
  const account = userStore.getAccount ?? ''
  if (isOpenNewWindow) {
    const sign = await getRsaSignOfAccount(account)
    const url = `${getUrl(path)}?${sign}`
    openWindow(url)
    return
  }

  to(path)
}

function getUrl(path: string) {
  return `${import.meta.env.VITE_LEYO_PAY_SERVER}/#${path}`
}

function getImageUrl(name: string) {
  return new URL(`../../../assets/images/${name}`, import.meta.url).href
}

const titleSrc = computed(() => {
  return getImageUrl('logo-title.svg')
})
const iconSrc = computed(() => {
  return getImageUrl('logo.svg')
})

function getIcon(item: MenuRoute) {
  return item.route.meta.icon as string
}

const collapsedWidth = ref(64)
const getRealWidth = computed(() => {
  return props.collapsed ? unref(collapsedWidth) : 225
})

const layoutSiderStyleClass = computed(() => {
  const cls = ['bg-white', 'side-wrap']
  if (props.hidden) {
    cls.push('hidden')
  }
  return cls
})

/** 过滤hideInMenu = true的菜单 */
const filterHideInMenu = (routes: MenuRoute[]) => {
  return routes.filter((item) => !item.route.meta.hideInMenu)
}

/** 获取显示的菜单名字 */
const getMenuName = (item: MenuRoute) => {
  const { name, route } = item
  return route?.meta?.menuName || name
}
</script>

<template>
  <ALayoutSider
    :collapsed="collapsed"
    :class="layoutSiderStyleClass"
    :width="getRealWidth"
    :collapsed-width="collapsedWidth"
    :trigger="null"
    collapsible
  >
    <div class="flex items-center justify-center py-6 logo-wrap gap-x-[4px]">
      <img :src="iconSrc" />
      <img v-if="!collapsed" :src="titleSrc" />
    </div>
    <AMenu
      v-model:open-keys="openKeys"
      :selected-keys="selectedKeys"
      mode="inline"
      theme="light"
      class="page-scroll text-[#282828]"
    >
      <template v-for="item in menuRoutes" :key="item.id">
        <template v-if="item.child?.length">
          <ASubMenu :key="item.route.path" :class="`guide-menu-class-${String(item.route.name)}`">
            <template #icon>
              <span class="anticon">
                <Icon :icon="getIcon(item)" size="24" />
              </span>
            </template>
            <template #title>{{ getMenuName(item) }}</template>
            <AMenuItem
              v-for="subItem in filterHideInMenu(item.child)"
              :key="subItem.route.path"
              @click.stop="handleSelectMenu(subItem.route)"
            >
              {{ getMenuName(subItem) }}
            </AMenuItem>
          </ASubMenu>
        </template>
        <template v-else>
          <AMenuItem
            :key="item.route.redirect ? (item.route.redirect as string) : item.route.path"
            @click.stop="handleSelectMenu(item.route)"
          >
            <template #icon>
              <span style="display: flex">
                <Icon :icon="getIcon(item)" size="24" />
              </span>
            </template>
            <span>{{ getMenuName(item) }}</span>
          </AMenuItem>
        </template>
      </template>
    </AMenu>
  </ALayoutSider>
</template>

<style lang="less" scoped>
.logo {
  line-height: 28px;
}
.logo-wrap .logo-text {
  flex: none;
  height: 28px;
  line-height: 28px;
  transition: all 0.2s;
}
.ant-layout-sider-collapsed .logo-wrap .logo-text {
  width: 0;
  margin-left: 0;
  overflow: hidden;
  opacity: 0;
}

.side-wrap {
  box-shadow: 4px 0px 14px 0 rgba(0, 0, 0, 0.06);
  z-index: 1000;
  .page-scroll {
    height: calc(100vh - 86px);
    overflow-y: auto;
    overflow-x: hidden;
    &::-webkit-scrollbar-thumb {
      background-color: rgba(169, 205, 254, 1);
    }
    &::-webkit-scrollbar-thumb:hover {
      background-color: rgba(169, 205, 254, 0.8);
    }
  }
}
</style>

<style lang="scss">
@import '@/styles/var/colors.scss';
.ant-menu-vertical > .ant-menu-item,
.ant-menu-vertical-left > .ant-menu-item,
.ant-menu-vertical-right > .ant-menu-item,
.ant-menu-inline > .ant-menu-item,
.ant-menu-vertical > .ant-menu-submenu > .ant-menu-submenu-title,
.ant-menu-vertical-left > .ant-menu-submenu > .ant-menu-submenu-title,
.ant-menu-vertical-right > .ant-menu-submenu > .ant-menu-submenu-title,
.ant-menu-inline > .ant-menu-submenu > .ant-menu-submenu-title {
  height: 52px;
  line-height: 52px;
}
.ant-menu.ant-menu-inline-collapsed > .ant-menu-item .ant-menu-item-icon,
.ant-menu.ant-menu-inline-collapsed
  > .ant-menu-item-group
  > .ant-menu-item-group-list
  > .ant-menu-item
  .ant-menu-item-icon,
.ant-menu.ant-menu-inline-collapsed
  > .ant-menu-item-group
  > .ant-menu-item-group-list
  > .ant-menu-submenu
  > .ant-menu-submenu-title
  .ant-menu-item-icon,
.ant-menu.ant-menu-inline-collapsed
  > .ant-menu-submenu
  > .ant-menu-submenu-title
  .ant-menu-item-icon,
.ant-menu.ant-menu-inline-collapsed > .ant-menu-item .anticon,
.ant-menu.ant-menu-inline-collapsed
  > .ant-menu-item-group
  > .ant-menu-item-group-list
  > .ant-menu-item
  .anticon,
.ant-menu.ant-menu-inline-collapsed
  > .ant-menu-item-group
  > .ant-menu-item-group-list
  > .ant-menu-submenu
  > .ant-menu-submenu-title
  .anticon,
.ant-menu.ant-menu-inline-collapsed > .ant-menu-submenu > .ant-menu-submenu-title .anticon {
  line-height: 52px;
}
.ant-menu-item .ant-menu-item-icon,
.ant-menu-submenu-title .ant-menu-item-icon,
.ant-menu-item .anticon,
.ant-menu-submenu-title .anticon {
  min-width: 24px;
}
.ant-menu.ant-menu-inline-collapsed .ant-menu-item-icon,
.ant-menu.ant-menu-inline-collapsed .anticon {
  height: 52px;
  display: flex;
  align-items: center;
}
.ant-menu.ant-menu-inline-collapsed > .ant-menu-item,
.ant-menu.ant-menu-inline-collapsed
  > .ant-menu-item-group
  > .ant-menu-item-group-list
  > .ant-menu-item,
.ant-menu.ant-menu-inline-collapsed
  > .ant-menu-item-group
  > .ant-menu-item-group-list
  > .ant-menu-submenu
  > .ant-menu-submenu-title,
.ant-menu.ant-menu-inline-collapsed > .ant-menu-submenu > .ant-menu-submenu-title {
  padding: 0 calc(50% - 24px / 2);
}
.ant-menu-submenu-arrow::before,
.ant-menu-submenu-arrow::after {
  height: 1px;
}
.ant-menu-submenu-expand-icon,
.ant-menu-submenu-arrow {
  color: #555;
}
.ant-menu-submenu-selected,
.ant-menu-item-selected {
  color: $colorPrimary;
  &:after {
    display: none;
  }
}
.ant-menu-item-selected {
  background-color: #e8f3ff !important;
}

.ant-layout {
  background-color: $bgPrimary;
}
.ant-menu-inline,
.ant-menu-vertical,
.ant-menu-vertical-left {
  border: none;
}
.ant-menu-sub.ant-menu-inline {
  background: #fff;
}
</style>
